import { getDefaultStore, type PrimitiveAtom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import { LOCAL_DB_META_KEY, LOCAL_DB_STORAGE_PREFIX } from '../config'
import type { LocalRow, SeedTables } from '../types/localDb.types'

/**
 * Database sementara local mode: satu atom jotai (`atomWithStorage`) per collection,
 * persisten di localStorage dengan key `mvdp-local-db:<collection>`.
 * Diakses di luar React lewat `getDefaultStore()` oleh handler mock API.
 */

const storage = createJSONStorage<LocalRow[]>(() => localStorage)
const tableAtoms = new Map<string, PrimitiveAtom<LocalRow[]>>()
const store = getDefaultStore()

const keyOf = (collection: string) => `${LOCAL_DB_STORAGE_PREFIX}:${collection}`

function tableAtom(collection: string): PrimitiveAtom<LocalRow[]> {
  const existing = tableAtoms.get(collection)
  if (existing) return existing
  const created = atomWithStorage<LocalRow[]>(keyOf(collection), [], storage, { getOnInit: true }) as PrimitiveAtom<
    LocalRow[]
  >
  tableAtoms.set(collection, created)
  return created
}

export function readTable(collection: string): LocalRow[] {
  return store.get(tableAtom(collection))
}

export function writeTable(collection: string, rows: LocalRow[]): void {
  store.set(tableAtom(collection), rows)
}

export function findRow(collection: string, id: unknown): LocalRow | undefined {
  if (id === null || id === undefined || id === '') return undefined
  const key = typeof id === 'object' ? (id as { id?: unknown }).id : id
  return readTable(collection).find(row => String(row.id) === String(key))
}

export function insertRow(collection: string, row: LocalRow): LocalRow {
  writeTable(collection, [...readTable(collection), row])
  return row
}

export function updateRow(collection: string, id: string, patch: Record<string, unknown>): LocalRow | undefined {
  let updated: LocalRow | undefined
  writeTable(
    collection,
    readTable(collection).map(row => {
      if (String(row.id) !== String(id)) return row
      updated = { ...row, ...patch, id: row.id }
      return updated
    })
  )
  return updated
}

export function deleteRow(collection: string, id: string): boolean {
  const rows = readTable(collection)
  const next = rows.filter(row => String(row.id) !== String(id))
  writeTable(collection, next)
  return next.length !== rows.length
}

type LocalDbMeta = { seedVersion: string; seededAt: string; collections: string[] }

export function readMeta(): LocalDbMeta | null {
  try {
    const raw = localStorage.getItem(LOCAL_DB_META_KEY)
    return raw ? (JSON.parse(raw) as LocalDbMeta) : null
  } catch {
    return null
  }
}

/** Mengganti seluruh isi local DB dengan seed (dipakai saat versi seed berubah atau reset manual). */
export function applySeed(seed: SeedTables, seedVersion: string): void {
  const previous = readMeta()?.collections ?? []
  for (const collection of previous) {
    if (!(collection in seed)) writeTable(collection, [])
  }
  for (const [collection, rows] of Object.entries(seed)) {
    writeTable(
      collection,
      rows.map(row => ({ ...row }))
    )
  }
  const meta: LocalDbMeta = {
    seedVersion,
    seededAt: new Date().toISOString(),
    collections: Object.keys(seed)
  }
  localStorage.setItem(LOCAL_DB_META_KEY, JSON.stringify(meta))
}
